import { Chip, ChipProps, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { formatRssi } from '../utils/string'

/**
 * The props of the ruuvi tag signal indicator.
 */
interface RuuviTagSignalChipProps extends Omit<ChipProps, 'label' | 'color' | 'children'> {
  /**
   * The RSSI value.
   */
  rssi: number | null
}

/**
 * A signal indicator for ruuvi tag.
 */
export default function RuuviTagSignalChip({ rssi, ...props }: RuuviTagSignalChipProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * Indicates if the signal is excellent.
   */
  const isExcellentSignal = rssi !== null && rssi > -60

  /**
   * Indicates if the signal is good.
   */
  const isGoodSignal = rssi !== null && rssi <= -60 && rssi > -80

  /**
   * Indicates if the signal is fair.
   */
  const isFairSignal = rssi !== null && rssi <= -80 && rssi > -90

  /**
   * Indicates if the signal is poor.
   */
  const isPoorSignal = rssi !== null && rssi <= -90

  /**
   * Get the message to be displayed in the tooltip.
   */
  const message = isExcellentSignal
    ? t('excellentSignal')
    : isGoodSignal
      ? t('goodSignal')
      : isFairSignal
        ? t('fairSignal')
        : isPoorSignal
          ? t('poorSignal')
          : t('noSignal')

  return (
    <Tooltip title={formatRssi(rssi)} arrow>
      {isExcellentSignal || isGoodSignal ? (
        <Chip label={message} color="success" {...props} />
      ) : isGoodSignal ? (
        <Chip label={message} color="success" {...props} />
      ) : isFairSignal ? (
        <Chip label={message} color="warning" {...props} />
      ) : (
        <Chip label={message} color="error" {...props} />
      )}
    </Tooltip>
  )
}
